import { pluralizeWithCount } from "@common/helpers/string";
import type { BoardMoveRefusal } from "./Board.types";

const countCards = (count: number) => pluralizeWithCount("card", count);

/**
 * Why the cards of a group drop stayed, as one sentence: each distinct
 * reason once, in board order ("Only done tasks can move to Deliver.
 * Canceled tasks can't be moved.").
 */
export const getRefusalReasons = <TItem>(
    refusals: ReadonlyArray<BoardMoveRefusal<TItem>>,
) =>
    Array.from(
        new Set(
            refusals.map(({ reason }) =>
                reason.endsWith(".") ? reason : `${reason}.`,
            ),
        ),
    ).join(" ");

interface PartialDropDescription<TItem> {
    /** What the surface calls an item ("task"). */
    noun: string;
    movedCount: number;
    refusals: ReadonlyArray<BoardMoveRefusal<TItem>>;
    /** Where the cards went; left out when the sentence doesn't say. */
    cellTitle?: string;
}

/**
 * "Moved 3 tasks to Prep, 1 couldn't move. {reasons}", or "1 task couldn't
 * move. {reasons}" when none moved.
 */
export const describePartialDrop = <TItem>({
    noun,
    movedCount,
    refusals,
    cellTitle,
}: PartialDropDescription<TItem>) => {
    const destination = cellTitle === undefined ? "" : " to " + cellTitle;
    if (movedCount === 0) {
        return `${pluralizeWithCount(noun, refusals.length)} couldn't move${destination}. ${getRefusalReasons(refusals)}`;
    }
    return `Moved ${pluralizeWithCount(noun, movedCount)}${destination}, ${refusals.length} couldn't move. ${getRefusalReasons(refusals)}`;
};

export const announceGroupPickUp = (count: number) =>
    `Picked up ${countCards(count)}.`;

/** An arrow key toward another lane while a group's lanes are fixed. */
export const GROUP_LANES_KEPT = "Selected cards keep their lanes.";

export const announceGroupReturn = (count: number) =>
    `${countCards(count)} returned.`;

/** A drop that moved nothing: "2 cards returned.", then why any card stayed. */
export const announceGroupStay = <TItem>(
    count: number,
    refusals: ReadonlyArray<BoardMoveRefusal<TItem>>,
) =>
    refusals.length === 0
        ? announceGroupReturn(count)
        : `${announceGroupReturn(count)} ${getRefusalReasons(refusals)}`;

export const announceGroupCancel = (count: number) =>
    `Move canceled. ${announceGroupReturn(count)}`;

export const announceGroupRefusedDrop = ({
    count,
    cellTitle,
    reason,
}: {
    count: number;
    cellTitle: string;
    reason: string;
}) => `Can't move ${countCards(count)} to ${cellTitle}. ${reason}`;

interface GroupDropDescription<TItem> {
    movedCount: number;
    refusals: ReadonlyArray<BoardMoveRefusal<TItem>>;
    cellTitle: string;
}

/**
 * A drop that moved cards: "Moved 3 cards to Prep.", or with refusals
 * "Moved 3 cards to Prep, 1 couldn't move. {reasons}".
 */
export const announceGroupDrop = <TItem>({
    movedCount,
    refusals,
    cellTitle,
}: GroupDropDescription<TItem>) =>
    refusals.length === 0
        ? `Moved ${countCards(movedCount)} to ${cellTitle}.`
        : describePartialDrop({
              noun: "card",
              movedCount,
              refusals,
              cellTitle,
          });
